/**
 * 合集卡片组件
 *
 * 视觉沿用首页 `.vcard` 三段式结构（封面 / 标题 / 标签 + 操作）。
 * 与视频卡片的差异：
 * - 封面右下角显示 "N 集" 而不是时长
 * - 右上角徽章显示可见性（private / public）
 * - 管理操作支持 编辑 / 删除（仅合集所有者）
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveAssetUrl } from '../utils/assetUrl';
import { DEFAULT_COVER_PATH, handleCoverError } from '../utils/videoCover';
import type { Collection } from '../types/collection';

const getLanguageName = (langCode: string): string => {
  const map: Record<string, string> = {
    zh: '中文',
    en: '英语',
    ja: '日语',
    ko: '韩语',
    es: '西班牙语',
    fr: '法语',
    de: '德语',
    ru: '俄语',
  };
  return map[langCode] || langCode;
};

interface CollectionCardProps {
  collection: Collection;
  /** 当前登录用户 ID（用于决定是否显示管理按钮） */
  currentUserId?: string | null;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  /**
   * 进入合集详情页时携带的"返回路径"。
   * 由调用方（首页公开合集列表 / 个人页我的合集 等）传入；
   * CollectionDetail 的"返回"按钮会优先用它。
   */
  returnTo?: string;
  /**
   * 是否显示"公开 / 私有"徽章。默认 true。
   * 首页"合集" tab 是统一只看 public 的窗口，徽章在那边没有信息量，
   * 调用方可显式传入 false 关闭。
   */
  showVisibilityBadge?: boolean;
  /** 为 true 时点击卡片不进入详情，改为触发 onLoginRequired（未登录浏览首页场景） */
  requireLoginToOpen?: boolean;
  onLoginRequired?: () => void;
}

export const CollectionCard: React.FC<CollectionCardProps> = ({
  collection,
  currentUserId,
  onEdit,
  onDelete,
  returnTo,
  showVisibilityBadge = true,
  requireLoginToOpen = false,
  onLoginRequired,
}) => {
  const navigate = useNavigate();

  const cover = collection.coverUrl
    ? resolveAssetUrl(collection.coverUrl) || DEFAULT_COVER_PATH
    : DEFAULT_COVER_PATH;

  const isOwner = !!currentUserId && currentUserId === collection.userId;

  const handleOpen = () => {
    if (requireLoginToOpen) {
      onLoginRequired?.();
      return;
    }
    navigate(
      `/collections/${collection.id}`,
      returnTo ? { state: { returnTo } } : undefined,
    );
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(collection.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(collection.id);
  };

  return (
    <div className="vcard" onClick={handleOpen}>
      <div className="vcard__thumb">
        <img
          src={cover}
          alt={collection.name}
          onError={handleCoverError}
          loading="lazy"
          decoding="async"
        />
        <div className="thumbnail-placeholder" style={{ display: 'none' }}>
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M3 4h13v2H3V4zm0 5h13v2H3V9zm0 5h7v2H3v-2zm15-9v14l6-7-6-7z" />
          </svg>
        </div>

        {/* 可见性徽章（调用方可关闭）— 左上角，与「我的视频」卡片视觉一致 */}
        {showVisibilityBadge && (
          <div
            className={`video-visibility-badge ${
              collection.visibility === 'public'
                ? 'video-visibility-badge--public'
                : 'video-visibility-badge--private'
            }`}
            title={collection.visibility === 'public' ? '已公开' : '仅自己可见'}
          >
            {collection.visibility === 'public' ? '公开' : '私有'}
          </div>
        )}

        {/* 总集数 */}
        <div className="video-duration">
          <span>{collection.itemCount} 集</span>
        </div>
      </div>

      <div className="vcard__title" data-text={collection.name} />

      <div className="vcard__tags">
        {collection.sourceLanguage && (
          <span className="vcard__tag vcard__tag--lang">
            {getLanguageName(collection.sourceLanguage)}
          </span>
        )}

        {isOwner && (
          <div className="vcard__admin-actions">
            <button
              className="vcard__admin-action vcard__admin-action--edit"
              onClick={handleEdit}
              title="编辑合集"
              aria-label="编辑合集"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button
              className="vcard__admin-action vcard__admin-action--delete"
              onClick={handleDelete}
              title="删除合集"
              aria-label="删除合集"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
